import React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import {
  MenuItem,
  Menu,
  useTheme,
  useMediaQuery,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Drawer,
  Hidden,
  Container,
  Box
} from '@material-ui/core';
import { Link } from 'react-router-dom'
import logo from '../assets/img/logo.png'
import { useAuth, useContext } from '../context';
import MenuIcon from '@material-ui/icons/Menu';
import gravatar from 'gravatar';
import { useTranslation } from 'react-i18next';
import ReceiptIcon from '@material-ui/icons/Receipt';
import AssessmentIcon from '@material-ui/icons/Assessment';
import SettingsIcon from '@material-ui/icons/Settings';

const drawerWidth = 240

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    main: {
      display: 'flex',
      height: '100%',
      width: '100%',
    },
    appBar: {
      marginLeft: drawerWidth,
      [theme.breakpoints.up('sm')]: {
        width: `calc(100% - ${drawerWidth}px)`
      }
    },
    draweSectionTitle: {
      padding: '10px 0 5px 20px'

    },
    menuButton: {
      marginRight: 20,
      [theme.breakpoints.up('sm')]: {
        display: 'none'
      }
    },
    toolbar: {
      display: 'flex'
    },
    grow: {
      flexGrow: 1
    },
    drawer: {
      [theme.breakpoints.up('sm')]: {
        width: drawerWidth,
        flexShrink: 0
      }
    },
    drawerPaper: {
      width: drawerWidth
    },
    toolbarSpacer: {
      ...theme.mixins.toolbar,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    logo: {
      maxWidth: '95%',
      maxHeight: '100%'
    },
    expansionDetails: {
      padding: 0
    },
  })
);


const ListItemLink = ({ icon, primary, to, click }: { icon: any, primary: string, to: string, click: () => void }) => {
  const CustomLink = React.useMemo(
    () =>
      React.forwardRef<any, any>((linkProps, ref) => (
        <Link ref={ref} to={to} {...linkProps} />
      )),
    [to],
  );
  return (
    <ListItem button component={CustomLink}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText color='black' primary={primary} />
    </ListItem>
  )
}

export const Layout = ({ children, name }: { children: any, name: string }) => {
  const classes = useStyles();
  const { t } = useTranslation('layout');
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const auth = useAuth();
  const context = useContext();

  const [anchorEl, setAnchorEl] = React.useState<EventTarget | null>(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  }

  const handleProfileMenuOpen = (target: EventTarget) => {
    setAnchorEl(target);
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  }

  const isMenuOpen = Boolean(anchorEl);

  const drawer = ({ mobile }: { mobile: boolean }) => (
    <>
      <div className={classes.toolbarSpacer}>
        <img className={classes.logo} src={logo} />
      </div>
      <Divider />
      <Typography className={classes.draweSectionTitle} variant='h6'>Settings</Typography>
      <Divider />
      <ListItemLink
        click={() => mobile && handleDrawerToggle()}
        to='/fees'
        primary={t('FEES')}
        icon={<SettingsIcon />}
      />

      <List>
        <Divider />
        <Typography className={classes.draweSectionTitle} variant='h6'>Direct Banking</Typography>
        <Divider />
        <ListItemLink
          click={() => mobile && handleDrawerToggle()}
          to='/orders'
          primary={t('ORDERS')}
          icon={<ReceiptIcon />}
        />
        <ListItemLink
          click={() => mobile && handleDrawerToggle()}
          to='/reports'
          primary={t('REPORTS')}
          icon={<AssessmentIcon />}
        />
      </List>
    </>
  )

  return (
    <div className={classes.main}>
      <AppBar position='fixed' className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <IconButton
            color='inherit'
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant='h6' color='inherit' className={classes.grow}>
            {t(name)}
          </Typography>
          <IconButton
            aria-owns={isMenuOpen ? 'material-appbar' : undefined}
            aria-haspopup='true'
            onClick={(event) => {
              handleProfileMenuOpen(event.target)
            }}
            color='inherit'
          >
            <Avatar alt={context.data.userEmail} src={gravatar.url(context.data.userEmail!, { d: 'identicon' })} />
          </IconButton>
          <Menu
            anchorEl={anchorEl as Element}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={() => {
              window.location.href = auth.getLogoutUri();
            }}>{t('LOGOUT')}</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <nav className={classes.drawer}>
        <Drawer
          variant='temporary'
          anchor={'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{ keepMounted: true }}
        >
          {drawer({ mobile: true })}
        </Drawer>
        <Hidden xsDown implementation='css'>
          <Drawer
            variant='permanent'
            className={classes.drawer}
            classes={{ paper: classes.drawerPaper }}
          >
            {drawer({ mobile: false })}
          </Drawer>
        </Hidden>
      </nav>
      <Box marginTop={10} width={'100%'}>
        <Container>
          {children}
        </Container>
      </Box>
    </div>
  )
};

